import { Grid, Stack, Checkbox } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import { useRecoilState } from "recoil";
import { useState } from "react";
import { TextField } from "../shared/TextField/TextField";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  CheckboxLabelWrapper,
  handleKeyDown,
  NewRequisitionTextField,
  NewRequisitionTextFieldLabel,
  PatientSubLabel,
  StyledFormLabelPatient,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import { checkDateValidation, checkDateValidationError } from "./helpers";
import { patientCheckboxAlignmentSx } from "./testPanelStyles";

const PatientInformationFamilyHistory: React.FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const [checkDOBError, setCheckDOBError] = useState(false);

  const { requisitionForm } = newRequisition;

  const onChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewRequisition({
      ...newRequisition,
      requisitionForm: {
        ...newRequisition.requisitionForm,
        [e.target.name]: e.target.checked,
      },
    });
  };
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewRequisition({
      ...newRequisition,
      requisitionForm: {
        ...newRequisition.requisitionForm,
        [e.target.name]: e.target.value,
      },
    });
  };

  return (
    <>
      <Grid container spacing={3} sx={{ py: 2 }} columns={12}>
        <Grid item xs={12}>
          <StyledFormLabelPatient
            disabled={disabled}
            sx={{ fontFamily: "light" }}
          >
            Family History
          </StyledFormLabelPatient>
          <br />
          <PatientSubLabel
            disabled={disabled}
            id="new-test-patient-family-history"
          >
            If there is a family history of genetic disorders, indicate below:
          </PatientSubLabel>
          <br />
          <br />
          <FormGroup>
            <TextField
              disabled={disabled}
              label=""
              fullWidth
              value={requisitionForm.familyHistoryGeneticDisorders}
              name="familyHistoryGeneticDisorders"
              onChange={onChange}
              multiline
              minRows={3}
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Grid>
        <StyledFormLabelPatient
          disabled={disabled}
          sx={{ fontFamily: "light" }}
        >
          Reported carrier
        </StyledFormLabelPatient>
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 12, md: 16 }}
          sx={{ mb: { xs: 4, sm: 0 } }}
        >
          <Grid
            item
            xs={4}
            sm={1}
            md={2}
            component={Stack}
            container
            direction="column"
            justifyContent="center"
          >
            <PatientSubLabel disabled={disabled}>
              Mother of Baby
            </PatientSubLabel>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierPatientAlpha}
                    name="reportedCarrierPatientAlpha"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-rc-at"
                label={GetTypographyContent({
                  content: "alpha thal",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2} sm={3} md={4}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierPatientSickle}
                    name="reportedCarrierPatientSickle"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-rc-hbb"
                label={GetTypographyContent({
                  content: "sickle cell / HBB",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierPatientSMA}
                    name="reportedCarrierPatientSMA"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-rc-sma"
                label={GetTypographyContent({
                  content: "SMA",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierPatientCF}
                    name="reportedCarrierPatientCF"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-rc-cf"
                label={GetTypographyContent({
                  content: "CF",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid
            sx={{
              flexGrow: 1,
              paddingTop: "5px !important",
              marginBottom: "20px",
            }}
            item
          >
            <TextField
              disabled={disabled}
              id="new-test-patient-rc-sv"
              fullWidth
              focused
              placeholder="Mother of baby specific variant"
              variant="standard"
              label=""
              value={requisitionForm.reportedCarrierPatientVariant}
              name="reportedCarrierPatientVariant"
              onChange={onChange}
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 12, md: 16 }}
        >
          <Grid
            item
            xs={4}
            sm={1}
            md={2}
            component={Stack}
            container
            direction="column"
            justifyContent="center"
          >
            <PatientSubLabel disabled={disabled}>
              Father of Baby
            </PatientSubLabel>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierFOBAlpha}
                    name="reportedCarrierFOBAlpha"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-fob-at"
                label={GetTypographyContent({
                  content: "alpha thal",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={2} sm={3} md={4}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierFOBSickle}
                    name="reportedCarrierFOBSickle"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-fob-sc"
                label={GetTypographyContent({
                  content: "sickle cell / HBB",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierFOBSMA}
                    name="reportedCarrierFOBSMA"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-fob-sma"
                label={GetTypographyContent({
                  content: "SMA",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2} sm={2} md={3}>
            <FormGroup>
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={requisitionForm.reportedCarrierFOBCF}
                    name="reportedCarrierFOBCF"
                    onChange={onChangeCheckBox}
                  />
                }
                id="new-test-patient-fob-cf"
                label={GetTypographyContent({
                  content: "CF",
                  size: "medium",
                  textColor: "greyMedium",
                })}
              />
            </FormGroup>
          </Grid>
          <Grid sx={{ flexGrow: 1, paddingTop: "5px !important" }} item>
            <TextField
              disabled={disabled}
              id="new-test-patient-fob-sv"
              fullWidth
              focused
              placeholder="Father of baby specific variant"
              variant="standard"
              label=""
              value={requisitionForm.reportedCarrierFOBVariant}
              name="reportedCarrierFOBVariant"
              onChange={onChange}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <StyledFormLabelPatient
          disabled={disabled}
          sx={{ fontFamily: "light", marginTop: "20px" }}
        >
          Link to Reproductive Partner (if applicable){" "}
          <span className="label_subtext">
            {" "}
            Include first, last name, and DOB
          </span>
        </StyledFormLabelPatient>
        <Grid
          container
          spacing={3}
          sx={{ py: 2, ...patientCheckboxAlignmentSx }}
          columns={{ xs: 4, sm: 10, md: 12 }}
        >
          <Grid item md={2} sm={4} xs={4}>
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.maternalReproductivePartner}
                  name="maternalReproductivePartner"
                  onChange={onChangeCheckBox}
                  disabled={
                    disabled || requisitionForm.paternalReproductivePartner
                  }
                />
              }
              label={GetTypographyContent({
                content: "Maternal",
                size: "medium",
                textColor: "greyMedium",
              })}
            />
          </Grid>
          <Grid item md={2} sm={4} xs={4}>
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.paternalReproductivePartner}
                  name="paternalReproductivePartner"
                  onChange={onChangeCheckBox}
                  disabled={
                    disabled || requisitionForm.maternalReproductivePartner
                  }
                />
              }
              label={GetTypographyContent({
                content: "Paternal",
                size: "medium",
                textColor: "greyMedium",
              })}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          sx={{ py: 2 }}
          columns={{ xs: 4, sm: 10, md: 12 }}
          style={
            requisitionForm.maternalReproductivePartner ||
            requisitionForm.paternalReproductivePartner
              ? {}
              : { opacity: 0.5, pointerEvents: "none" }
          }
        >
          <Grid item md={4} sm={4} xs={4}>
            <NewRequisitionTextField
              disabled={disabled}
              label={
                <NewRequisitionTextFieldLabel>
                  First Name*
                  {newRequisition.isError &&
                    (requisitionForm.maternalReproductivePartner ||
                      requisitionForm.paternalReproductivePartner) &&
                    !requisitionForm.reproductivePartnerFirstName && (
                      <ExclamationIcon />
                    )}
                </NewRequisitionTextFieldLabel>
              }
              autoComplete="nope"
              variant="standard"
              size="small"
              error={
                newRequisition.isError &&
                (requisitionForm.maternalReproductivePartner ||
                  requisitionForm.paternalReproductivePartner) &&
                !requisitionForm.reproductivePartnerFirstName
                  ? "Error"
                  : ""
              }
              placeholder="First Name"
              onKeyDown={handleKeyDown}
              fullWidth
              focused
              value={requisitionForm.reproductivePartnerFirstName}
              name="reproductivePartnerFirstName"
              onChange={onChange}
            />
          </Grid>
          <Grid item md={4} sm={4} xs={4}>
            <NewRequisitionTextField
              disabled={disabled}
              label={
                <NewRequisitionTextFieldLabel>
                  Last Name*
                  {newRequisition.isError &&
                    (requisitionForm.maternalReproductivePartner ||
                      requisitionForm.paternalReproductivePartner) &&
                    !requisitionForm.reproductivePartnerLastName && (
                      <ExclamationIcon />
                    )}
                </NewRequisitionTextFieldLabel>
              }
              autoComplete="nope"
              error={
                newRequisition.isError &&
                (requisitionForm.maternalReproductivePartner ||
                  requisitionForm.paternalReproductivePartner) &&
                !requisitionForm.reproductivePartnerLastName
                  ? "Error"
                  : ""
              }
              variant="standard"
              size="small"
              placeholder="Last Name"
              fullWidth
              onKeyDown={handleKeyDown}
              focused
              value={requisitionForm.reproductivePartnerLastName}
              name="reproductivePartnerLastName"
              onChange={onChange}
            />
          </Grid>
          <Grid item md={4} sm={4} xs={4}>
            <NewRequisitionTextField
              disabled={disabled}
              label={
                <NewRequisitionTextFieldLabel>
                  Date of Birth*
                  {newRequisition.isError &&
                    (requisitionForm.maternalReproductivePartner ||
                      requisitionForm.paternalReproductivePartner) &&
                    !requisitionForm.reproductivePartnerDateOfBirth && (
                      <ExclamationIcon />
                    )}
                </NewRequisitionTextFieldLabel>
              }
              autoComplete="nope"
              error={
                newRequisition.isError &&
                (requisitionForm.maternalReproductivePartner ||
                  requisitionForm.paternalReproductivePartner) &&
                !requisitionForm.reproductivePartnerDateOfBirth
                  ? "Error"
                  : ""
              }
              variant="standard"
              size="small"
              type="date"
              $noDateValue={!requisitionForm.dateOfBirth}
              placeholder="MM-DD-YYYY"
              fullWidth
              focused
              value={requisitionForm.reproductivePartnerDateOfBirth}
              name="reproductivePartnerDateOfBirth"
              onChange={onChange}
              onBlur={() => {
                setCheckDOBError(checkDateValidation("DOB", requisitionForm));
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
export default PatientInformationFamilyHistory;
